"use client";
import { BasicProps } from "components/types/BasicProps";
import { generalLiterals } from "literals";
import { FC, useEffect, useMemo, useState } from "react";
import CtaSecondary from "components/CtaSecondary";
import CtaPrimary from "components/CtaPrimary";
import { useTokenBalance } from "components/providers/BalanceProvider";
import useDepositedBalance from "components/hooks/useDepositedBalance";
import Link from "next/link";
import displayApy from "utils/displayApy";
import TokenIcon from "components/icons/TokenIcon";
import { CurrencyGeneralData } from "logic/EarnModule";
import displayTvl from "utils/displayTvl";
import { IErc20Currency } from "logic/types/currency/ICurrency";
import track from "logic/track";
import { ReserveState } from "contracts/UTokenFactoryContract";
import classNames from "classnames";
import currenciesModule from "logic/CurrenciesModule";
import { NativeCurrency } from "logic/types/currency/Currency";
import NetworkIcon from "components/icons/NetworkIcon";
import { NetworkConfig } from "config/app.config";
import { verisModule } from "clients/verisModule";

type Props = BasicProps & {
  currency: IErc20Currency;
  generalData?: CurrencyGeneralData;
};

const { balancePlaceholder, apyPlaceholder, withdrawCta, supplyCta } =
  generalLiterals.earn;

const CurrencyRow: FC<Props> = (props) => {
  const { currency, generalData } = props;
  const isWrappedNative = currency.isWrappedNative;

  const nativeCurrency = useMemo<NativeCurrency>(
    () => currenciesModule.getNativeCurrency(currency.chain.id),
    []
  );

  const network = useMemo<NetworkConfig>(
    () => verisModule.getNetwork(currency.chain.id),
    []
  );

  const { balance, forceBalanceUpdate } = useTokenBalance(
    isWrappedNative ? nativeCurrency.id : currency.id
  );

  const { balance: deposit, forceUpdate } = useDepositedBalance(currency);

  const baseCurrencyUrl = `/earn/${currency.chain.name}/${
    isWrappedNative ? nativeCurrency.name : currency.name
  }?opt=`;

  const currencyLabel = isWrappedNative ? nativeCurrency.label : currency.label;

  const depositToShow = useMemo<string>(
    () =>
      deposit !== undefined
        ? `${currency.formatAmount(deposit)} ${currencyLabel}`
        : balancePlaceholder,
    [deposit]
  );

  const balanceToShow = useMemo<string>(
    () =>
      balance !== undefined
        ? `${currency.formatAmount(balance)} ${currencyLabel}`
        : balancePlaceholder,
    [balance]
  );

  const tvlToShow = useMemo<string>(
    () => (generalData ? displayTvl(generalData.tvl) : "$-.--"),
    [generalData]
  );

  const apyToShow = useMemo<string>(
    () => (generalData ? displayApy(generalData.apy) : apyPlaceholder),
    [generalData]
  );

  const utilizationRate = useMemo<string>(
    () => (generalData ? `${generalData?.utilizationRate}%` : "-.--%"),
    [generalData]
  );

  // const isActive = false;
  const isActive = useMemo<boolean>(
    () => !generalData || generalData.reserveState == ReserveState.ACTIVE,
    [generalData]
  );

  const opacityStyles = isActive ? "" : "opacity-50";

  useEffect(() => {
    forceUpdate();
    forceBalanceUpdate();
  }, []);

  return (
    <tr className="bg-secondary text-base font-medium">
      <td
        className={classNames(
          "flex py-6 px-[30px] font-medium items-center",
          opacityStyles
        )}
      >
        <NetworkIcon className="w-3 h-3 mr-1" network={network} />
        <TokenIcon className="w-6 h-6 mr-2" currency={currency} />
        {isWrappedNative ? nativeCurrency.label : currency.label}
      </td>
      <td
        className={classNames(
          "py-4 pr-[30px] text-right font-bold",
          opacityStyles
        )}
      >
        {apyToShow}
      </td>
      <td
        className={classNames(
          "py-4 pr-[30px] font-normal text-right",
          opacityStyles
        )}
      >
        {tvlToShow}
      </td>
      <td
        className={classNames(
          "py-4 pr-[30px] font-normal text-right",
          opacityStyles
        )}
      >
        {utilizationRate}
      </td>
      <td
        className={classNames(
          "py-4 pr-[30px] font-normal text-right",
          opacityStyles
        )}
      >
        {depositToShow}
      </td>
      <td
        className={classNames(
          "py-4 pr-[30px] font-normal text-right",
          opacityStyles
        )}
      >
        {balanceToShow}
      </td>

      <td className="w-fit">
        <div className="w-fit flex justify-center gap-x-5">
          <a onClick={() => track.click(`earn_click_deposit_${currency.name}`)}>
            <Link href={`${baseCurrencyUrl}supply`}>
              <CtaPrimary className="w-40" disabled={!isActive}>
                {supplyCta}
              </CtaPrimary>
            </Link>
          </a>
          <a
            onClick={() => track.click(`earn_click_withdraw_${currency.name}`)}
          >
            <Link href={`${baseCurrencyUrl}withdraw`}>
              <CtaSecondary className="w-40" disabled={!isActive}>
                {withdrawCta}
              </CtaSecondary>
            </Link>
          </a>
        </div>
      </td>
    </tr>
  );
};

export default CurrencyRow;
